import {
  PlaygroundLocalMsg,
  usePlaygroundLocalMsg,
} from "@faire/prototype-playground/platform/localization";
import { CartEmpty } from "@faire/slate-icons/CartEmpty";
import { StarFilled } from "@faire/slate-icons/StarFilled";
import { TopShopTrophy } from "@faire/slate-icons/TopShopTrophy";
import { Avatar } from "@faire/slate/components/avatar";
import { Button } from "@faire/slate/components/button";
import { ProgressBar } from "@faire/slate/components/progress-bar";
import { tsv } from "@faire/slate/lib/tsv";

import type { FaireBrandEntity } from "@faire/playground-entity-db/entity_types/brand";
import type { FaireProductEntity } from "@faire/playground-entity-db/entity_types/product";
import { getAllEntities, getEntityByToken } from "../../../data/get_entity";

/** Brand facts shown in the lockup, resolved from entity data with fallbacks. */
export interface BrandGroupSummary {
  name: string;
  avatarUrl?: string;
  location: string;
  rating: number;
  reviewCount: number;
  isTopShop: boolean;
  /** Order minimum in dollars. */
  minimum: number;
}

/**
 * How the lockup exposes the brand's cart:
 * - "pill": one tappable pill holding the cart count and minimum tracker
 *   (Figma 9687:322855).
 * - "button": the minimum tracker beside a "View cart" secondary button
 *   (Figma 9645:291449).
 */
export type BrandCartAction = "pill" | "button";

interface BrandGroupHeaderProps {
  brand: BrandGroupSummary;
  /** Dollar value of this brand's items currently in the cart. */
  cartSubtotal: number;
  /** Units of this brand currently in the cart. */
  cartItemCount: number;
  /** Whether the side cart currently open belongs to this brand. */
  isCartOpen?: boolean;
  cartAction?: BrandCartAction;
  /** Opens this brand's side cart, or closes it when it is already open. */
  onToggleCart: () => void;
}

/**
 * Brand lockup used when saved products are organized by brand: avatar, name,
 * location · rating (reviews) · Top shop, with the cart button on the right
 * (Figma 9687:322855): a pill holding the cart count and the order-minimum
 * tracker that opens this brand's side cart.
 */
export const BrandGroupHeader = ({
  brand,
  cartSubtotal,
  cartItemCount,
  isCartOpen = false,
  cartAction = "pill",
  onToggleCart,
}: BrandGroupHeaderProps) => {
  const avatarLabel = usePlaygroundLocalMsg("{brandName} logo", {
    brandName: brand.name,
  });

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <Avatar
          size="small"
          label={avatarLabel}
          image={brand.avatarUrl ? { url: brand.avatarUrl } : undefined}
        />
        <div className="flex flex-col">
          <h2 className="type-fs-subheading-medium text-fs-text-primary">
            {brand.name}
          </h2>
          <div className="type-fs-paragraph text-fs-text-primary flex items-center gap-1">
            <span>{brand.location}</span>
            <span aria-hidden>·</span>
            <span className="flex items-center gap-1">
              <StarFilled width="12" height="12" />
              <span>{brand.rating.toFixed(1)}</span>
              <span className="underline">({brand.reviewCount})</span>
            </span>
            {brand.isTopShop ? (
              <>
                <span aria-hidden>·</span>
                <span className="flex items-center gap-1">
                  <TopShopTrophy width="12" height="12" />
                  <PlaygroundLocalMsg defaultMessage="Top shop" />
                </span>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {cartAction === "pill" ? (
        <MinimumCartButton
          minimum={brand.minimum}
          cartSubtotal={cartSubtotal}
          cartItemCount={cartItemCount}
          isCartOpen={isCartOpen}
          onClick={onToggleCart}
        />
      ) : (
        <MinimumTrackerWithButton
          minimum={brand.minimum}
          cartSubtotal={cartSubtotal}
          isCartOpen={isCartOpen}
          onClick={onToggleCart}
        />
      )}
    </div>
  );
};

// --- Tracker + View cart button --------------------------------------------

interface MinimumTrackerWithButtonProps {
  minimum: number;
  cartSubtotal: number;
  isCartOpen: boolean;
  onClick: () => void;
}

/** Minimum tracker beside a secondary "View cart" button (Figma 9645:291449). */
const MinimumTrackerWithButton = ({
  minimum,
  cartSubtotal,
  isCartOpen,
  onClick,
}: MinimumTrackerWithButtonProps) => {
  const progress = minimum > 0 ? Math.min(1, cartSubtotal / minimum) : 1;

  return (
    <div className="flex h-12 items-center gap-4">
      <div className="flex w-30 flex-col justify-center">
        <span className="type-fs-label text-fs-text-primary whitespace-nowrap">
          <PlaygroundLocalMsg
            defaultMessage="{minimum} minimum"
            values={{ minimum: formatDollars(minimum) }}
          />
        </span>
        <div className="py-1">
          <ProgressBar progress={progress} variant="success" showIcon />
        </div>
      </div>
      <Button
        variant="secondary"
        size="small"
        aria-expanded={isCartOpen}
        onClick={onClick}
      >
        {isCartOpen ? (
          <PlaygroundLocalMsg defaultMessage="Hide cart" />
        ) : (
          <PlaygroundLocalMsg defaultMessage="View cart" />
        )}
      </Button>
    </div>
  );
};

// --- Cart button ------------------------------------------------------------

interface MinimumCartButtonProps {
  minimum: number;
  cartSubtotal: number;
  cartItemCount: number;
  isCartOpen: boolean;
  onClick: () => void;
}

/** Cart count plus minimum tracker in one tappable pill (Figma "Cart button"). */
const MinimumCartButton = ({
  minimum,
  cartSubtotal,
  cartItemCount,
  isCartOpen,
  onClick,
}: MinimumCartButtonProps) => {
  const progress = minimum > 0 ? Math.min(1, cartSubtotal / minimum) : 1;
  const openLabel = usePlaygroundLocalMsg(
    "View cart, {count} items, {minimum} minimum",
    { count: cartItemCount, minimum: formatDollars(minimum) }
  );
  const closeLabel = usePlaygroundLocalMsg("Hide cart");

  return (
    <button
      type="button"
      aria-label={isCartOpen ? closeLabel : openLabel}
      aria-expanded={isCartOpen}
      onClick={onClick}
      className={cartButtonStyle.cls()}
    >
      <span className={cartPillStyle.cls()}>
        <CartEmpty width="16" height="16" />
        <span className="type-fs-label-medium text-fs-text-primary">
          {cartItemCount}
        </span>
      </span>
      <span className="flex flex-col items-start justify-center">
        <span className="type-fs-label text-fs-text-primary whitespace-nowrap">
          <PlaygroundLocalMsg
            defaultMessage="{minimum} minimum"
            values={{ minimum: formatDollars(minimum) }}
          />
        </span>
        <span className="w-full py-1">
          <ProgressBar progress={progress} variant="success" showIcon />
        </span>
      </span>
    </button>
  );
};

const cartButtonStyle = tsv({
  base: [
    "bg-fs-surface-primary border-fs-border-muted hover:border-fs-action-border-hover",
    "flex items-center gap-2 rounded-3xl border py-1 pr-6 pl-2 text-left transition-colors",
  ],
});

const cartPillStyle = tsv({
  base: [
    "text-fs-text-primary flex h-8 items-center gap-1 rounded-4xl px-2",
    "border-fs-action-border-inverse border-2",
  ],
});

// --- Brand resolution -------------------------------------------------------

const DEFAULT_BRAND_LOCATION = "Los Angeles, CA";
const DEFAULT_BRAND_RATING = 4.8;
const DEFAULT_BRAND_REVIEW_COUNT = 122;
const DEFAULT_BRAND_MINIMUM = 200;

let brandsByName: Map<string, FaireBrandEntity> | undefined;

const findBrandByName = (brandName: string): FaireBrandEntity | undefined => {
  brandsByName ??= new Map(
    getAllEntities("brand").map((brand) => [brand.name, brand])
  );
  return brandsByName.get(brandName);
};

/**
 * Builds the lockup summary for a brand from its entity when one exists
 * (by token, then by name); otherwise fills in from the product and sensible
 * defaults so every saved brand still gets a complete lockup.
 */
export const resolveBrandGroupSummary = (
  brandName: string,
  products: FaireProductEntity[]
): BrandGroupSummary => {
  const firstProduct = products[0];
  const brandToken = products.find((product) => product.brandToken)?.brandToken;
  const brandEntity =
    (brandToken ? getEntityByToken(brandToken, "brand") : undefined) ??
    findBrandByName(brandName);

  return {
    name: brandName,
    avatarUrl:
      brandEntity?.avatarUrl ??
      brandEntity?.logoUrl ??
      firstProduct?.brandAvatarUrl ??
      firstProduct?.imageUrl,
    location: brandEntity?.location ?? DEFAULT_BRAND_LOCATION,
    rating: brandEntity?.rating ?? firstProduct?.rating ?? DEFAULT_BRAND_RATING,
    reviewCount:
      brandEntity?.reviewCount ??
      firstProduct?.reviewCount ??
      DEFAULT_BRAND_REVIEW_COUNT,
    isTopShop: brandEntity?.isTopShop ?? true,
    minimum: brandEntity?.minimumOrderDollars ?? DEFAULT_BRAND_MINIMUM,
  };
};

const formatDollars = (amount: number): string =>
  `$${Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2)}`;
